import type { CanvasOp, ClientMessage, ServerMessage } from "@refinement-canvas/domain";
import type { RealtimeClient } from "./realtime-client";

interface CreateOptions {
  readonly websocketUrl: string;
  readonly createSocket?: ((url: string) => WebSocket) | undefined;
}

const SOCKET_OPEN = 1;
const MAX_PENDING_MESSAGES = 100;

// WebSocket 経由のリアルタイム通信クライアント。
// PO Board の websocket-realtime.ts を踏襲し、サーバ権威キュー方式に拡張する。
// - 接続: GET /api/canvases/:id/realtime-session でチケット発行 → wss?ticket=... で接続
// - 送信: 永続 op は op.apply、一時 op は op.transient で送信
// - 受信: viewer.* / op.commit / op.transient / op.reject / bootstrap.snapshot / resync.required
//
// 骨格段階では接続スケルトンと message 配信ループのみ提供する。
export function createWebSocketRealtimeClient(options: CreateOptions): RealtimeClient {
  let socket: WebSocket | null = null;
  const handlers = new Set<(message: ServerMessage) => void>();
  const pendingMessages: ClientMessage[] = [];

  const connect = async (canvasId: string, ticket?: string): Promise<void> => {
    const params = new URLSearchParams({ canvasId });
    if (ticket) params.set("ticket", ticket);
    const url = `${options.websocketUrl}?${params.toString()}`;
    socket = (options.createSocket ?? ((socketUrl: string) => new WebSocket(socketUrl)))(url);
    return new Promise((resolve, reject) => {
      if (!socket) return reject(new Error("socket 初期化失敗"));
      socket.onopen = () => {
        flushPendingMessages();
        resolve();
      };
      socket.onerror = (event) => reject(event);
      socket.onclose = () => {
        if (socket?.readyState !== SOCKET_OPEN) {
          reject(new Error("WebSocket 接続が確立前に切断されました。"));
        }
      };
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as ServerMessage;
          handlers.forEach((h) => h(message));
        } catch (e) {
          console.warn("[realtime] invalid message", e);
        }
      };
    });
  };

  const disconnect = (): void => {
    if (socket?.readyState === SOCKET_OPEN) {
      socket.send(JSON.stringify({ type: "viewer.leave" } satisfies ClientMessage));
    }
    pendingMessages.length = 0;
    socket?.close();
    socket = null;
  };

  const sendMessage = (message: ClientMessage): void => {
    if (socket?.readyState !== SOCKET_OPEN) {
      enqueuePendingMessage(message);
      return;
    }
    socket.send(JSON.stringify(message));
  };

  const sendOp = (op: CanvasOp): void => {
    sendMessage({ type: "op.apply", op });
  };

  const onMessage = (handler: (message: ServerMessage) => void): (() => void) => {
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
    };
  };

  return { connect, disconnect, sendOp, sendMessage, onMessage };

  function enqueuePendingMessage(message: ClientMessage): void {
    // CanvasStage は接続完了前にマウントされるため、初回 viewer.sync を落とさない。
    // 上限を超えた場合は古いものから捨て、未接続中の操作でメモリが増え続けることを防ぐ。
    pendingMessages.push(message);
    if (pendingMessages.length > MAX_PENDING_MESSAGES) {
      pendingMessages.shift();
    }
  }

  function flushPendingMessages(): void {
    if (socket?.readyState !== SOCKET_OPEN) return;
    const messages = pendingMessages.splice(0);
    for (const message of messages) {
      socket.send(JSON.stringify(message));
    }
  }
}
